import { useMemo } from "react";
import Link from "next/link";
// eslint-disable-next-line no-restricted-imports -- TODO: update to `next/navigation` before using this component in App Router
import { useRouter } from "next/router";
import qs, { ParsedQs } from "qs";

import { Action, Category } from "constants/events";
import { useEventTracker } from "hooks/useEventTracker";
import useFilters from "hooks/useFilters";
import parseNextRouterAsPath from "utils/parseNextRouterAsPath";

import DropdownV2 from "@/components/botanic/DropdownV2";

const SortDropdown = ({
  availableSorts,
  eventCategory,
  sortDisplayStrings,
  onToggleDropdown,
  sortsAsLinks,
}: {
  availableSorts: string[];
  eventCategory: Category;
  sortDisplayStrings: Record<string, string>;
  onToggleDropdown?: () => void;
  sortsAsLinks?: boolean;
}) => {
  const { asPath } = useRouter();
  const { query } = parseNextRouterAsPath<ParsedQs>(asPath);
  const { publishEvent } = useEventTracker();
  const { selectFilterValue, addFilter } = useFilters({
    shallowRouting: false,
  });

  const validAvailableSorts = useMemo(
    () =>
      availableSorts.filter((value) =>
        Object.keys(sortDisplayStrings).includes(value),
      ),
    [availableSorts, sortDisplayStrings],
  );

  if (validAvailableSorts.length === 0) {
    return <></>;
  }

  const sortValue = selectFilterValue("sort") || validAvailableSorts[0];
  const pathWithoutQuery = asPath.split("?")[0];
  const { sort: _oldSort, page: _oldPage, ...rest } = query;

  const handleClick = (value: string) => {
    // If we're loading these as buttons, we need to include the filters when we handle click
    !sortsAsLinks && addFilter({ sort: value });

    publishEvent({
      action: Action.click,
      category: eventCategory,
      label: sortDisplayStrings[value].toLowerCase(),
    });
  };

  return (
    <DropdownV2
      data-testid="sort-dropdown"
      label="Sort by"
      variant="small"
      preselectedOption={String(sortValue)}
      onToggleDropdown={onToggleDropdown}
      options={validAvailableSorts}
      onItemRender={(option) => {
        const orderedParams = {
          sort: option,
          ...rest,
        };

        const isHighestRatedWithNoFilters =
          option === "highest-rated" && !query.filter && !query.sort;

        return {
          as: Link,
          children: sortDisplayStrings[option],
          "data-testid": `listing-sort-${option}`,
          href: `${pathWithoutQuery}?${qs.stringify(orderedParams, { encode: false })}`,
          onClick: () => handleClick(option),
          rel: !isHighestRatedWithNoFilters ? "nofollow" : undefined,
        };
      }}
    />
  );
};

export default SortDropdown;
